import { supabase } from '../lib/supabase';

export const notificationService = {
  /**
   * Get all notifications for current user
   */
  async getUserNotifications(userId) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.select(`
        *,
        project:projects(name),
        phase:phases(phase_name)
      `)
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching notifications:', error);
      throw new Error(error?.message || 'Failed to fetch notifications');
    }

    return data?.map(notification => ({
      id: notification?.id,
      userId: notification?.user_id,
      projectId: notification?.project_id,
      phaseId: notification?.phase_id,
      title: notification?.title,
      message: notification?.message,
      type: notification?.type,
      isRead: notification?.is_read,
      createdAt: notification?.created_at,
      projectName: notification?.project?.name,
      phaseName: notification?.phase?.phase_name
    }));
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(userId) {
    const { count, error } = await supabase
      ?.from('notifications')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false);

    if (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }

    return count || 0;
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    const { error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true, updated_at: new Date()?.toISOString() })
      ?.eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
      throw new Error(error?.message || 'Failed to mark notification as read');
    }
  },

  /**
   * Mark all notifications as read for user
   */
  async markAllAsRead(userId) {
    const { error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true, updated_at: new Date()?.toISOString() })
      ?.eq('user_id', userId)
      ?.eq('is_read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      throw new Error(error?.message || 'Failed to mark all notifications as read');
    }
  },

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('id', notificationId);

    if (error) {
      console.error('Error deleting notification:', error);
      throw new Error(error?.message || 'Failed to delete notification');
    }
  },

  /**
   * Subscribe to real-time notifications
   */
  subscribeToNotifications(userId, callback) {
    const uniqueId = Math.random()?.toString(36)?.slice(2, 9);
    const channel = supabase
      ?.channel(`notifications:${userId}:${uniqueId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(payload?.new);
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from notifications
   */
  unsubscribeFromNotifications(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};